import Edit16 from '@carbon/icons-react/es/edit/16';
import OverflowMenuHorizontal16 from '@carbon/icons-react/es/overflow-menu--horizontal/16';
import Reply16 from '@carbon/icons-react/es/reply/16';
import { Dropdown, Popover, Tooltip } from 'antd';
import React, { FC, ReactNode, useEffect, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import { Reaction, UserMessage } from 'api/conversations/types';
import { Button } from 'components/atoms/button';
import { Menu } from 'components/atoms/dropdown';
import { Reactions } from 'components/atoms/reactions';
import { SpotlightActionIcon } from 'components/organisms/spotlight/spotlight-action-icon';
import { useWithAccess } from 'hooks/rbac/useWithAccess';
import { useIsCustomerPortalEnabled } from 'hooks/use-feature';
import { useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';

const KebabMenuWrapper = styled.div`
  display: flex;
  box-shadow: var(--shadow-08);

  .ant-btn {
    border: none;
  }
`;

type IProps = {
  attachTo: HTMLDivElement | null;
  children: ReactNode;
  comment: UserMessage;
  readOnly: boolean;
  visible: boolean;
  onAddReaction(reactionType: Reaction): void;
  onMarkReply?(): void;
  onPerformEdit(editState: boolean): void;
  onPerformDelete(): void;
  showConfirm?(): void;
  isStarred?: boolean;
  onStar?: () => void;
};

export const KebabMenu: FC<IProps> = ({
  attachTo,
  children,
  comment,
  readOnly,
  visible = false,
  onAddReaction,
  onMarkReply,
  onPerformEdit,
  showConfirm,
  isStarred,
  onStar,
}) => {
  const [popoverVisibility, setPopoverVisibility] = useState(false);
  const replyText = useTranslation('conversations.send_reply');
  const addReactionsText = useTranslation('reactions.add');

  const [showReactionTooltip, toggleShowReactionTooltip] = useState(false);
  const [isReactionTriggerVisible, setReactionTriggerVisible] = useState(false);
  const currentUser = useUser();
  const { hasAccessToResource } = useWithAccess();
  const isCpEnabled = useIsCustomerPortalEnabled();

  const hasCommentCreateAccess = hasAccessToResource({
    rule: 'CONVERSATION_COMMENT:CREATE',
  });

  useEffect(() => {
    setPopoverVisibility(visible && popoverVisibility);
  }, [popoverVisibility, visible]);

  const handleMenuItemClick = (e) => {
    switch (e.key) {
      case 'edit':
        return onPerformEdit(true);
      case 'delete':
        return showConfirm?.();
      default:
        return null;
    }
  };

  const MenuItems = () => (
    <Menu className="conversations-kebab" onClick={handleMenuItemClick}>
      <Menu.Item
        key="edit"
        data-cy="chat.message.edit"
        icon={<Edit16 className="anticon anticon-user" />}
      >
        <FormattedMessage id="edit" />
      </Menu.Item>
      <Menu.ItemDelete key="delete" data-cy="chat.message.delete" />
    </Menu>
  );

  return (
    <Popover
      destroyTooltipOnHide
      content={
        !readOnly &&
        hasCommentCreateAccess && (
          <KebabMenuWrapper>
            {onStar && isCpEnabled ? (
              <Tooltip
                title={<FormattedMessage id={isStarred ? 'unstar' : 'star'} />}
              >
                <Button
                  data-cy="chat.message.star"
                  icon={<SpotlightActionIcon isActive={isStarred} />}
                  size="small"
                  type="text"
                  onClick={onStar}
                />
              </Tooltip>
            ) : null}
            <Tooltip
              destroyTooltipOnHide
              onVisibleChange={toggleShowReactionTooltip}
              visible={showReactionTooltip && !isReactionTriggerVisible}
              title={addReactionsText}
            >
              <Reactions>
                <Reactions.Add
                  attachTo={attachTo}
                  type="text"
                  onAddReaction={onAddReaction}
                  onReactionVisibilityChange={setReactionTriggerVisible}
                />
              </Reactions>
            </Tooltip>
            <Tooltip title={replyText}>
              <Button
                data-cy="chat.message.reply"
                data-convpane-reply-btn
                icon={<Reply16 />}
                size="small"
                type="text"
                onClick={onMarkReply}
              />
            </Tooltip>

            {comment.user.userId === currentUser.user.userId && (
              <Dropdown overlay={MenuItems} trigger={['click']}>
                <Button
                  data-cy="chat.message.actions"
                  aria-label="ellipsis"
                  data-testid="ellipsis"
                  icon={<OverflowMenuHorizontal16 />}
                  size="small"
                  type="text"
                />
              </Dropdown>
            )}
          </KebabMenuWrapper>
        )
      }
      overlayClassName="popover__compact kebab__menu"
      visible={popoverVisibility}
      placement="topRight"
      getPopupContainer={attachTo ? () => attachTo : undefined}
      onVisibleChange={setPopoverVisibility}
    >
      <div>{children}</div>
    </Popover>
  );
};
